import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

/**
 * 404 — "SIGNAL LOST".
 * Subtle stars + the same engineered aesthetic as the rest of the site.
 */

const EASE = [0.16, 1, 0.3, 1];

export default function NotFound() {
  useEffect(() => {
    document.title = "Signal lost — Parabolum";
  }, []);

  const stars = useMemo(
    () =>
      Array.from({ length: 80 }, (_, i) => {
        const seed = i * 9301 + 49297;
        const r1 = ((seed % 233280) / 233280) * 100;
        const r2 = (((seed * 7) % 233280) / 233280) * 100;
        const r3 = ((seed * 13) % 100) / 100;
        return {
          left: r1,
          top: r2,
          size: r3 > 0.92 ? 2 : 1,
          opacity: 0.06 + ((i * 7) % 22) / 100,
          delay: ((i * 17) % 800) / 1000,
        };
      }),
    [],
  );

  const particles = useMemo(
    () =>
      Array.from({ length: 14 }, (_, i) => ({
        left: ((i * 73) % 100),
        top: ((i * 41) % 100),
        size: 1 + (i % 3),
        duration: 8 + (i % 6),
        delay: (i % 10) * 0.4,
      })),
    [],
  );

  return (
    <main
      data-testid="not-found-page"
      className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#030305] text-white"
    >
      {/* faint engineered grid */}
      <div className="grid-faint pointer-events-none absolute inset-0 opacity-[0.04]" />

      {/* static stars */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        {stars.map((s, i) => (
          <motion.span
            key={`s-${i}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: s.opacity }}
            transition={{ duration: 1.6, delay: s.delay, ease: "easeOut" }}
            className="absolute rounded-full bg-white"
            style={{
              left: `${s.left}%`,
              top: `${s.top}%`,
              width: `${s.size}px`,
              height: `${s.size}px`,
            }}
          />
        ))}
      </div>

      {/* very subtle drifting particles */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        {particles.map((p, i) => (
          <motion.span
            key={`p-${i}`}
            initial={{ opacity: 0, y: 0 }}
            animate={{ opacity: [0, 0.35, 0], y: -40 }}
            transition={{
              duration: p.duration,
              delay: p.delay,
              repeat: Infinity,
              ease: "linear",
            }}
            className="absolute rounded-full bg-white"
            style={{
              left: `${p.left}%`,
              top: `${p.top}%`,
              width: `${p.size}px`,
              height: `${p.size}px`,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 mx-auto flex max-w-3xl flex-col items-center px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mb-7 flex items-center gap-3"
        >
          <span className="h-px w-8 bg-white/30" />
          <span className="font-body text-[11px] uppercase tracking-[0.35em] text-white/45">
            404
          </span>
          <span className="h-px w-8 bg-white/30" />
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease: EASE, delay: 0.1 }}
          className="font-display text-5xl font-bold leading-[1] tracking-tight text-white sm:text-6xl md:text-7xl lg:text-[5.4rem]"
        >
          SIGNAL{" "}
          <span className="font-serif-italic font-normal text-white/55">
            lost.
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.22 }}
          className="font-body mt-7 max-w-md text-[15px] leading-relaxed text-white/50"
        >
          The requested object could not be located.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.35 }}
          className="mt-12"
        >
          <Link
            to="/"
            data-testid="not-found-return"
            data-cursor="hover"
            className="group inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/[0.02] px-7 py-4 font-body text-[13px] uppercase tracking-[0.22em] text-white/85 backdrop-blur-[2px] transition-all duration-500 hover:border-white/35 hover:bg-white/[0.05] hover:text-white"
          >
            Return to studio
            <span className="inline-block transition-transform duration-500 group-hover:translate-x-1">
              →
            </span>
          </Link>
        </motion.div>
      </div>
    </main>
  );
}
